<template>
    <div>
        <span v-if="!props.row">{{ value }}</span>
        <TooltipComp
            v-if="hasErrorInInterval() && props.showActiveInterval"
            :text="intervalErrorTooltipText"
            class="pe-auto float-start me-1 mt-1"
        >
            <span class="alert alert-warning p-0 px-1 display-6"><IconComp :icon="featureIcons.WARNING" /></span>
        </TooltipComp>
        <span
            v-for="(alias, index) in row?.StationAliases"
            :key="alias.ID"
            :class="{
                'bg-warning-subtle':
                    (isErrorInAliasForEmit(alias.Alias) && !props.showActiveInterval) ||
                    (isErrorInInterval(alias) && props.showActiveInterval),
            }"
        >
            <template v-if="props.showActiveInterval">
                <DateComp
                    :value="alias.ActiveFrom"
                    :format="'DATETIME'"
                />
                <span> - </span>
                <DateComp
                    :value="alias.ActiveTo"
                    :format="'DATETIME'"
                />
            </template>
            <template v-else>
                <a
                    v-if="isAliasLinkableForEmit(alias.Alias)"
                    :href="getAliasLinkForEmit(alias.Alias)"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {{ alias.Alias }}
                </a>
                <template v-else>
                    <TooltipComp :text="getToolTipText(alias.Alias)">
                        {{ alias.Alias }}
                    </TooltipComp>
                </template>
            </template>
            <span v-if="index < row.StationAliases.length - 1">, </span>
        </span>
    </div>
</template>
<script setup lang="ts">
import type { Station, StationAlias } from '@/objects/generated';
import { TooltipComp, DateComp, IconComp } from '@/components';
import { useEventCommonStoreRef } from '@/stores';
import { featureIcons } from '@/components/Menu/featureicons';
import { computed } from 'vue';
import { $tm } from '@/locize';

const isErrorInAliasForEmit = (alias: string | null | undefined): boolean => {
    return alias?.startsWith('876') === true && alias?.length !== 12;
};

const isAliasLinkableForEmit = (alias: string | null | undefined): boolean => {
    return alias?.startsWith('876') === true && alias.length === 12;
};

const getAliasLinkForEmit = (alias: string | null | undefined): string => {
    return `http://195.159.103.189/?Y=${encodeURIComponent(alias?.slice(0, 9) ?? '')}`;
};

const getToolTipText = (alias: string | null | undefined): string => {
    if (isErrorInAliasForEmit(alias)) {
        return 'stationalias.error_in_emit_alias';
    }
    return '';
};

const { Event } = useEventCommonStoreRef();

const isErrorInInterval = (alias: StationAlias): boolean => {
    if (!alias.ActiveFrom || !alias.ActiveTo) {
        return true;
    }
    if (alias.ActiveFrom >= alias.ActiveTo) {
        return true;
    }
    if (Event?.value?.EventStart && Event?.value?.EventEnd) {
        if (alias.ActiveFrom < Event.value.EventStart || alias.ActiveTo > Event.value.EventEnd) {
            return true;
        }
    }
    return false;
};

const hasErrorInInterval = (): boolean => {
    if (!props.row?.StationAliases || props.row.StationAliases.length === 0) {
        return false;
    }
    for (const alias of props.row.StationAliases) {
        if (isErrorInInterval(alias)) {
            return true;
        }
    }
    return false;
};

const intervalErrorTooltipText = computed(() => {
    if (!props.row?.StationAliases || props.row.StationAliases.length === 0) {
        return '';
    }
    for (const alias of props.row.StationAliases) {
        if (isErrorInInterval(alias)) {
            return $tm('stationalias.error_in_active_interval');
        }
    }
    return '';
});

const props = defineProps<{
    value: string;
    row: Station;
    showActiveInterval?: boolean;
}>();
</script>
